// src/features/classes/ClassCard.jsx
import React from "react";
import { formatCurrency } from "../../utils/formatCurrency";

const FALLBACK_IMAGE = "/assets/placeholder-class.jpg";

const ClassCard = ({ classItem, onEnroll }) => {
  const {
    title,
    description,
    price,
    grade,
    subject,
    duration,
    includesAssessmentBooks,
    lessonType,
    pricingModel,
    cover,
  } = classItem;

  // Map lessonType to display text and badge color
  const lessonTypeInfo = {
    video: { label: "Video Lesson", color: "bg-blue-600" },
    group: { label: "Group Class", color: "bg-purple-600" },
    "one-on-one": { label: "One-on-One", color: "bg-green-600" },
  };

  const typeInfo = lessonTypeInfo[lessonType] || { label: lessonType, color: "bg-gray-600" };

  // Format price display
  const priceDisplay =
    pricingModel === "hourly"
      ? `${formatCurrency(price)}/hour`
      : formatCurrency(price);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col h-full border border-gray-100">
      {/* Image */}
      <div className="relative h-48 overflow-hidden bg-gray-200">
        <img
          src={cover || FALLBACK_IMAGE}
          alt={title}
          className="w-full h-auto object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = FALLBACK_IMAGE;
          }}
        />
        {/* Lesson type badge (top left) */}
        <span
          className={`absolute top-2 left-2 ${typeInfo.color} text-white text-xs font-semibold px-2 py-1 rounded-full shadow`}
        >
          {typeInfo.label}
        </span>
        {/* Includes Books badge (top right) */}
        {includesAssessmentBooks && (
          <span className="absolute top-2 right-2 bg-green-600 text-white text-xs font-semibold px-2 py-1 rounded-full shadow">
            Includes Books
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-grow">
        <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2">
          {title}
        </h3>

        {/* Subject & Grade badges */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
            {subject}
          </span>
          <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded">
            Grade {grade}
          </span>
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{description}</p>

        {/* Duration & Price row */}
        <div className="flex justify-between items-center mb-4 text-sm">
          <span className="flex items-center text-gray-500">
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {duration}
          </span>
          <span className="font-bold text-lg text-gray-900">{priceDisplay}</span>
        </div>

        {/* Enroll Button */}
        {onEnroll && (
          <button
            onClick={() => onEnroll(classItem)}
            className="mt-auto w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Enroll Now
          </button>
        )}
      </div>
    </div>
  );
};

export default ClassCard;